// The four-way grammar of a sequence template block, shared by the editor's
// highlighting and its legend. The same grammar is enforced server-side in
// `apps/autoleads/services/mailing_copy/template_syntax.py`: keep both in sync.
//
// - `{{variable}}`: a merge field the agent resolves to real text. Loose
//   syntax on purpose, since the user types it by hand.
// - `[adaptive sentence]`: a blank the agent writes itself, following the
//   instruction inside the brackets.
// - `@plugin` / `@plugin.section`: a data reference, pointing at one section of
//   this prospect's plugin results and making it required for this block. This
//   file knows the shape only; the catalog of valid slugs lives in
//   `plugin-refs.ts`, so an unknown slug still parses as a reference (and is
//   flagged as a typo upstream) rather than silently becoming fixed copy.
// - anything else: fixed copy the agent must reproduce word for word.

export type TemplateSegmentKind =
  | "variable"
  | "adaptive"
  | "reference"
  | "fixed"

export type TemplateSegment = {
  kind: TemplateSegmentKind
  text: string
}

export type TemplateRef = {
  token: string
  slug: string
  option: string | null
}

// Case-insensitive on input: a hand-typed `@Audit` that failed to match would
// fall back into a FIXED span, and the server's fidelity lint would then demand
// the agent reproduce it verbatim. The lookbehind keeps email addresses out
// ("contact@site.fr" has a word character before the @) and keeps `{{@audit}}`
// a single variable instead of also collecting it as a reference.
const REF_NAME = "[A-Za-z][A-Za-z0-9_]*"
const REF_HEAD = "(?<![-\\w.@{])@"
const REF_SOURCE = `${REF_HEAD}${REF_NAME}(?:\\.${REF_NAME})?`

// The reference alternative comes last, so an `@` already inside `{{...}}`
// stays part of that variable. A `[...]` token is re-split below instead: its
// references keep their own color.
const VARIABLE_SOURCE = "\\{\\{[^{}]*\\}\\}"
const ADAPTIVE_SOURCE = "\\[[^[\\]]*\\]"
const TOKEN_RE = new RegExp(
  `${VARIABLE_SOURCE}|${ADAPTIVE_SOURCE}|${REF_SOURCE}`,
  "g",
)
const VARIABLE_RE = new RegExp(VARIABLE_SOURCE, "g")

// COLLECTION scans the raw text instead, mirroring `template_refs` server-side:
// a reference written inside an `[adaptive sentence]` instruction ("open on
// @audit.performance") is a real reference too, and the bracket token above
// would otherwise swallow it.
const REF_RE = new RegExp(`${REF_HEAD}(${REF_NAME})(?:\\.(${REF_NAME}))?`, "g")

function segmentKind(token: string): TemplateSegmentKind {
  if (token.startsWith("{{")) return "variable"
  if (token.startsWith("[")) return "adaptive"
  return "reference"
}

// A reference written inside an `[adaptive sentence]` is still a reference, so
// its color wins over the bracket's: the token is split back out of the
// adaptive span rather than painted as instruction text.
function splitAdaptive(token: string): TemplateSegment[] {
  const segments: TemplateSegment[] = []
  let cursor = 0
  for (const match of token.matchAll(REF_RE)) {
    const start = match.index
    if (start > cursor) {
      segments.push({ kind: "adaptive", text: token.slice(cursor, start) })
    }
    segments.push({ kind: "reference", text: match[0] })
    cursor = start + match[0].length
  }
  if (cursor < token.length) {
    segments.push({ kind: "adaptive", text: token.slice(cursor) })
  }
  return segments
}

export function parseTemplateSegments(text: string): TemplateSegment[] {
  const segments: TemplateSegment[] = []
  let cursor = 0
  for (const match of text.matchAll(TOKEN_RE)) {
    const start = match.index
    if (start > cursor) {
      segments.push({ kind: "fixed", text: text.slice(cursor, start) })
    }
    const kind = segmentKind(match[0])
    if (kind === "adaptive") {
      segments.push(...splitAdaptive(match[0]))
    } else {
      segments.push({ kind, text: match[0] })
    }
    cursor = start + match[0].length
  }
  if (cursor < text.length) {
    segments.push({ kind: "fixed", text: text.slice(cursor) })
  }
  return segments
}

// Slug and option of a `@plugin[.option]` token, lowercased like the server
// lowercases them.
export function parseRefToken(token: string): TemplateRef {
  const [slug, option] = token.slice(1).toLowerCase().split(".")
  return { token, slug, option: option ?? null }
}

// Every reference of a block, in order, whether or not it sits inside an
// adaptive instruction. Mirrors `template_refs` server-side: the two must agree
// or the wizard and the server disagree about the same template.
export function extractRefs(text: string): TemplateRef[] {
  return [...(text ?? "").matchAll(REF_RE)].map((match) => ({
    token: match[0],
    slug: match[1].toLowerCase(),
    option: match[2] ? match[2].toLowerCase() : null,
  }))
}

// Mirrors of the server's grammar helpers (`template_syntax.py`), which the
// save-time checks of `template-problems.ts` are built on. Same names, same
// rules: a block the editor passes is a block the server accepts.

// A fixed span shorter than this many words is glue around a variable, not
// checked verbatim by the lint (MAILING_TEMPLATE_LITERAL_MIN_WORDS).
export const TEMPLATE_LITERAL_MIN_WORDS = 4

function nonEmptyLines(block: string): string[] {
  return block
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter((word) => word !== "").length
}

// Fixed spans of a block, in order, blanks and variables removed.
export function templateLiterals(block: string): string[] {
  return block
    .split(TOKEN_RE)
    .map((part) => part.trim())
    .filter((part) => part !== "")
}

// Literals the lint holds every draft to, word for word.
export function enforceableLiterals(block: string): string[] {
  return templateLiterals(block).filter(
    (literal) => wordCount(literal) >= TEMPLATE_LITERAL_MIN_WORDS,
  )
}

// True when the block's last line is fixed text: the template ends the email
// itself and the agent writes no closing line under it.
export function definesClosing(block: string): boolean {
  const lines = nonEmptyLines(block)
  if (lines.length === 0) return false
  return !new RegExp(TOKEN_RE.source).test(lines[lines.length - 1])
}

// Question marks the fixed text already holds.
export function fixedQuestionCount(block: string): number {
  return templateLiterals(block).reduce(
    (count, literal) => count + literal.split("?").length - 1,
    0,
  )
}

export type GrammarBreak =
  | "nested_brackets"
  | "unbalanced_brackets"
  | "unbalanced_braces"

// How a block breaks the bracket grammar, null when it is sound. Only the
// innermost `[...]` is read as a blank, so a nested or unmatched bracket turns
// instruction text into fixed copy the agent must reproduce verbatim.
export function grammarBreak(block: string): GrammarBreak | null {
  const text = block.replace(VARIABLE_RE, " ")
  let depth = 0
  for (const char of text) {
    if (char === "[") {
      depth += 1
      if (depth > 1) return "nested_brackets"
    } else if (char === "]") {
      if (depth === 0) return "unbalanced_brackets"
      depth -= 1
    }
  }
  if (depth > 0) return "unbalanced_brackets"
  if (text.includes("{{") || text.includes("}}")) return "unbalanced_braces"
  return null
}

function startsUpper(text: string): boolean {
  const first = text.charAt(0)
  return first !== first.toLowerCase() && first === first.toUpperCase()
}

// True when some `[adaptive sentence]` closes a sentence of its own: at the end
// of its line, or followed by a new sentence. Only there can the agent ask a
// question the fixed text does not.
export function adaptiveEndsSentence(block: string): boolean {
  for (const match of block.matchAll(TOKEN_RE)) {
    if (!match[0].startsWith("[")) continue
    const rest = block
      .slice(match.index + match[0].length)
      .split("\n", 1)[0]
      .trimStart()
    if (
      rest === "" ||
      startsUpper(rest) ||
      new RegExp(`^(?:${TOKEN_RE.source})`).test(rest)
    ) {
      return true
    }
  }
  return false
}

// The last token of the block's last line and the fixed text after it.
export function lastLineParts(block: string): { token: string; tail: string } {
  const lines = nonEmptyLines(block)
  if (lines.length === 0) return { token: "", tail: "" }
  const last = lines[lines.length - 1]
  const tokens = [...last.matchAll(TOKEN_RE)]
  if (tokens.length === 0) return { token: "", tail: last }
  const final = tokens[tokens.length - 1]
  return {
    token: final[0],
    tail: last.slice(final.index + final[0].length).trim(),
  }
}
